import React, { useState, useEffect } from 'react';
import { useObjetivosSancor } from './useObjetivosSancor';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        maximumFractionDigits: 0
    }).format(Math.abs(amount || 0));
};

// --- ICONOS SVG ---
const IconTrophy = () => (
    <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4a5 5 0 005 5h4a5 5 0 005-5V3M19 7h2a2 2 0 012 2v1a4 4 0 01-4 4h-1M5 7H3a2 2 0 00-2 2v1a4 4 0 04 4h1m5 4v6m-4 0h8" />
    </svg>
);

const IconMoney = () => (
    <svg className="w-5 h-5 text-emerald-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const IconDocumentCheck = () => (
    <svg className="w-5 h-5 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const IconDocumentX = () => (
    <svg className="w-5 h-5 text-rose-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const IconCross = () => (
    <svg className="w-5 h-5 text-purple-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
);

const IconFilter = () => (
    <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
    </svg>
);

// --- COMPONENTE SKELETON LOADER CON SPINNER DE CARGA ---
const KpisSkeleton = () => (
    <div className="space-y-6 animate-pulse text-left">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6, 7].map(i => (
                <div key={i} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                    <div className="flex justify-between items-center">
                        <div className="h-4 bg-slate-200 rounded w-36" />
                        <div className="h-5 bg-slate-200 rounded-full w-12" />
                    </div>
                    <div className="h-8 bg-slate-200 rounded w-28" />
                    <div className="h-2 bg-slate-200 rounded-full w-full" />
                    <div className="pt-3 border-t border-slate-100 flex justify-between">
                        <div className="h-3 bg-slate-200 rounded w-16" />
                        <div className="h-3 bg-slate-200 rounded w-20" />
                    </div>
                </div>
            ))}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-12 h-52 flex items-center justify-center shadow-sm">
            <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider animate-pulse">Sincronizando Desafío SANCOR...</span>
            </div>
        </div>
    </div>
);

const KpisObjetivosSancor = () => {
    const {
        selectedCuatrimestreId,
        setSelectedCuatrimestreId,
        selectedOrganizador,
        setSelectedOrganizador,
        selectedPas,
        setSelectedPas,
        codigosSancorData,
        cuatrimestresList,
        loading,
        isDbLoaded,
        metrics,
        refetch
    } = useObjetivosSancor();

    const [expandedColabName, setExpandedColabName] = useState(null);
    const [activeDetailTab, setActiveDetailTab] = useState('ALTAS');
    const [activeTab, setActiveTab] = useState('RIESGOS');

    // Refresca automáticamente al montar la pantalla completa
    useEffect(() => {
        refetch();
    }, []);

    const toggleColabDetail = (colabName) => {
        if (expandedColabName === colabName) {
            setExpandedColabName(null);
        } else {
            setExpandedColabName(colabName);
            setActiveDetailTab('ALTAS');
        }
    };

    const ramosView = metrics?.isAgrupadoAutoMoto ? [
        { key: 'autosMotos', title: '🚗 Autos, Motos & Camiones', badgeBg: 'bg-indigo-50 text-indigo-700 border-indigo-200', barColor: 'bg-indigo-600' },
        { key: 'hogar', title: '🏡 Combinado Familiar / Hogar', badgeBg: 'bg-emerald-50 text-emerald-700 border-emerald-200', barColor: 'bg-emerald-600' },
        { key: 'comercio', title: '🏢 Integral de Comercio', badgeBg: 'bg-blue-50 text-blue-700 border-blue-200', barColor: 'bg-blue-600' },
        { key: 'ap', title: '🩹 Accidentes Personales', badgeBg: 'bg-amber-50 text-amber-700 border-amber-200', barColor: 'bg-amber-600' },
        { key: 'salud', title: '🏥 Salud', badgeBg: 'bg-rose-50 text-rose-700 border-rose-200', barColor: 'bg-rose-600' },
        { key: 'caucion', title: '🛡️ Caución', badgeBg: 'bg-purple-50 text-purple-700 border-purple-200', barColor: 'bg-purple-600' },
        { key: 'responsabilidadCivil', title: '⚖️ Responsabilidad Civil', badgeBg: 'bg-teal-50 text-teal-700 border-teal-200', barColor: 'bg-teal-600' }
    ] : [
        { key: 'autosCamion', title: '🚗 Autos & Camiones', badgeBg: 'bg-indigo-50 text-indigo-700 border-indigo-200', barColor: 'bg-indigo-600' },
        { key: 'motos', title: '🏍️ Motovehículos', badgeBg: 'bg-sky-50 text-sky-700 border-sky-200', barColor: 'bg-sky-600' },
        { key: 'hogar', title: '🏡 Combinado Familiar / Hogar', badgeBg: 'bg-emerald-50 text-emerald-700 border-emerald-200', barColor: 'bg-emerald-600' },
        { key: 'comercio', title: '🏢 Integral de Comercio', badgeBg: 'bg-blue-50 text-blue-700 border-blue-200', barColor: 'bg-blue-600' },
        { key: 'ap', title: '🩹 Accidentes Personales', badgeBg: 'bg-amber-50 text-amber-700 border-amber-200', barColor: 'bg-amber-600' },
        { key: 'salud', title: '🏥 Salud', badgeBg: 'bg-rose-50 text-rose-700 border-rose-200', barColor: 'bg-rose-600' },
        { key: 'caucion', title: '🛡️ Caución', badgeBg: 'bg-purple-50 text-purple-700 border-purple-200', barColor: 'bg-purple-600' },
        { key: 'responsabilidadCivil', title: '⚖️ Responsabilidad Civil', badgeBg: 'bg-teal-50 text-teal-700 border-teal-200', barColor: 'bg-teal-600' }
    ];

    // 🌟 EVALUACIÓN ESTRICTA DE CARGA INICIAL (Muestra el Skeleton si está consultando o aún no leyó la DB)
    const isInitialLoading = loading || !isDbLoaded || !metrics;

    return (
        <div className="space-y-6 text-left animate-fade-in">
            
            {/* CABECERA Y SELECTORES PRINCIPALES */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-amber-50 rounded-2xl border border-amber-100 shrink-0">
                        <IconTrophy />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl font-black text-slate-800 tracking-tight leading-tight">
                                Desafío Comercial Trimestral SANCOR 2026/2027
                            </h2>
                            {loading && (
                                <span className="flex h-2 w-2 relative">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                            Competencia Trimestral de Crecimiento Neto (Emisiones – Bajas)
                        </p>
                    </div>
                </div>

                {/* BOTONERA TRIMESTRAL */}
                <div className="flex items-center bg-slate-100 p-1.5 rounded-2xl border border-slate-200 shrink-0 flex-wrap gap-1">
                    {cuatrimestresList.map(q => {
                        const isSelected = q.id === selectedCuatrimestreId;
                        return (
                            <button
                                key={q.id}
                                onClick={() => setSelectedCuatrimestreId(q.id)}
                                className={`px-3 py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer ${
                                    isSelected 
                                        ? 'bg-white text-blue-900 shadow-sm border border-slate-200' 
                                        : 'text-slate-500 hover:text-slate-800'
                                }`}
                            >
                                {q.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* BARRA DE FILTROS COMERCIALES */}
            <div className="bg-white p-4 px-6 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                    <IconFilter />
                    <span className="text-xs font-black text-slate-700 uppercase tracking-wider">
                        Estructura Comercial SANCOR
                    </span>
                    {(selectedOrganizador || selectedPas) && (
                        <span className="bg-blue-100 text-blue-800 text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase ml-2">
                            Filtro Activo
                        </span>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Organizador:</label>
                        <select
                            value={selectedOrganizador}
                            onChange={(e) => {
                                setSelectedOrganizador(e.target.value);
                                setSelectedPas('');
                            }}
                            className="bg-slate-50 border border-slate-200 rounded-xl p-2 px-3 text-xs font-bold text-slate-800 outline-none focus:bg-white focus:border-blue-900 transition-colors cursor-pointer min-w-[180px]"
                        >
                            <option value="">TODOS (GENERAL)</option>
                            {codigosSancorData.organizadoresUnicos.map(org => (
                                <option key={org} value={org}>{org}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">PAS / Código:</label>
                        <select
                            value={selectedPas}
                            onChange={(e) => setSelectedPas(e.target.value)}
                            className="bg-slate-50 border border-slate-200 rounded-xl p-2 px-3 text-xs font-bold text-slate-800 outline-none focus:bg-white focus:border-blue-900 transition-colors cursor-pointer min-w-[180px]"
                        >
                            <option value="">TODOS LOS PAS</option>
                            {codigosSancorData.pasUnicos.map(pas => (
                                <option key={pas} value={pas}>{pas}</option>
                            ))}
                        </select>
                    </div>

                    {(selectedOrganizador || selectedPas) && (
                        <button
                            onClick={() => {
                                setSelectedOrganizador('');
                                setSelectedPas('');
                            }}
                            className="text-[10px] font-extrabold text-rose-500 hover:text-rose-700 underline uppercase tracking-wider px-2 cursor-pointer"
                        >
                            Limpiar Filtros
                        </button>
                    )}
                </div>
            </div>

            {/* BOTONERA DE PESTAÑAS SOLAPADAS */}
            <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setActiveTab('RIESGOS')}
                        className={`px-4 py-2 rounded-xl text-xs font-black transition-all cursor-pointer ${
                            activeTab === 'RIESGOS'
                                ? 'bg-amber-500 text-white shadow-md shadow-amber-500/20'
                                : 'bg-white text-slate-500 hover:bg-slate-100 border border-slate-200/80'
                        }`}
                    >
                        📊 Estadísticas por Riesgo Trimestral
                    </button>
                    <button
                        onClick={() => setActiveTab('PRODUCCION')}
                        className={`px-4 py-2 rounded-xl text-xs font-black transition-all cursor-pointer ${
                            activeTab === 'PRODUCCION'
                                ? 'bg-amber-500 text-white shadow-md shadow-amber-500/20'
                                : 'bg-white text-slate-500 hover:bg-slate-100 border border-slate-200/80'
                        }`}
                    >
                        💰 Estadísticas de Producción & Balance
                    </button>
                </div>
            </div>

            {/* RENDERIZADO CONDICIONAL ESTRICTO */}
            {isInitialLoading ? (
                <KpisSkeleton />
            ) : (
                <>
                    {/* VISTA 1: ESTADÍSTICAS POR RIESGO TRIMESTRAL */}
                    {activeTab === 'RIESGOS' && (
                        <div className="space-y-6 animate-fade-in">
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                {ramosView.map(ramo => {
                                    const data = metrics.riesgos[ramo.key];

                                    if (!data) return null;

                                    return (
                                        <div key={ramo.key} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between space-y-4 hover:shadow-md transition-all">
                                            <div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs font-extrabold text-slate-800">{ramo.title}</span>
                                                    <span className={`text-xs font-black px-2.5 py-0.5 rounded-full border ${ramo.badgeBg}`}>
                                                        {data.porcCuatri || 0}%
                                                    </span>
                                                </div>

                                                <div className="mt-3 flex items-baseline justify-between">
                                                    <h3 className="text-2xl font-black text-slate-900 font-mono">
                                                        {data.neto || 0} <span className="text-xs text-slate-400 font-normal">/ {data.metaCuatri || 0} netas</span>
                                                    </h3>
                                                </div>

                                                {/* CHIPS DE DESGLOSE EN CARD AGRUPADA */}
                                                {ramo.key === 'autosMotos' && (
                                                    <div className="flex flex-wrap items-center gap-2 pt-2 text-[11px] font-bold font-mono">
                                                        <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-100">
                                                            🚗 Autos: {data.autosSoloNeto || 0}
                                                        </span>
                                                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-100">
                                                            🏍️ Motos: {data.motosSoloNeto || 0}
                                                        </span>
                                                        <span className="bg-amber-50 text-amber-800 px-2 py-0.5 rounded border border-amber-200">
                                                            🚛 Camiones: {data.camionesSoloNeto || 0}
                                                        </span>
                                                    </div>
                                                )}

                                                <div className="w-full bg-slate-100 rounded-full h-2 mt-3 overflow-hidden">
                                                    <div 
                                                        className={`h-full transition-all duration-500 ${ramo.barColor}`}
                                                        style={{ width: `${Math.min(data.porcCuatri || 0, 100)}%` }}
                                                    />
                                                </div>
                                            </div>

                                            {/* CONTEO DE MOVIMIENTOS + NUEVAS VS. RENOVACIONES */}
                                            <div className="pt-3 border-t border-slate-100 space-y-2">
                                                <div className="flex items-center justify-between text-[11px] font-bold">
                                                    <span className="text-slate-400 uppercase text-[9px] font-black">Movimientos:</span>
                                                    <div className="flex items-center gap-2 font-mono">
                                                        <span className="text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                                                            +{data.altas || 0} altas
                                                        </span>
                                                        <span className="text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-100">
                                                            -{data.bajas || 0} bajas
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-2 text-xs font-bold pt-1 border-t border-dashed border-slate-100">
                                                    <div>
                                                        <span className="text-[9px] font-black text-slate-400 uppercase block">Nuevas:</span>
                                                        <span className="text-emerald-600 font-mono font-black">{data.nuevas || 0}</span>
                                                        <span className="text-slate-400 font-normal font-mono"> / {data.metaNuevas || 0}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-[9px] font-black text-slate-400 uppercase block">Pases Compañía:</span>
                                                        <span className="text-blue-600 font-mono font-black">{data.renovaciones || 0}</span>
                                                        <span className="text-slate-400 font-normal font-mono"> / {data.metaRenovaciones || 0}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* TARJETA DE CROSS-SELLING HOGAR */}
                            <div className="bg-white p-5 rounded-2xl border border-purple-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-3 bg-purple-50 rounded-2xl border border-purple-100">
                                        <IconCross />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">
                                            Cross-Selling Hogar (Venta Cruzada Pack)
                                        </h4>
                                        <p className="text-xs text-slate-400 font-medium mt-0.5">
                                            Porcentaje de pólizas de Combinado Familiar contratadas mediante venta cruzada (isPolizaPack)
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-6 font-mono shrink-0">
                                    <div className="text-right">
                                        <span className="text-[9px] text-slate-400 font-black uppercase tracking-wider block">Penetración</span>
                                        <span className="text-xl font-black text-purple-700">{metrics.crossSelling.ratio}%</span>
                                    </div>
                                    <div className="text-right border-l border-slate-200 pl-6">
                                        <span className="text-[9px] text-slate-400 font-black uppercase tracking-wider block">Packs / Total Hogar</span>
                                        <span className="text-base font-black text-slate-800">
                                            {metrics.crossSelling.totalCross} / {metrics.crossSelling.totalHogarAltas}
                                        </span>
                                    </div>
                                </div>
                            </div>

                        </div>
                    )}

                    {/* VISTA 2: ESTADÍSTICAS DE PRODUCCIÓN & BALANCE */}
                    {activeTab === 'PRODUCCION' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in">
                            
                            {/* BALANCE DE PRIMAS */}
                            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Balance de Primas SANCOR</span>
                                    <span className="p-2 bg-emerald-50 rounded-xl"><IconMoney /></span>
                                </div>
                                <div>
                                    <h3 className="text-2xl font-black text-emerald-600 font-mono">
                                        {formatCurrency(metrics.totalesGenerales.balancePrimaNeto)}
                                    </h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">Saldo Neto Comercial ($)</p>
                                </div>
                                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-mono">
                                    <span className="text-emerald-600 font-bold">+{formatCurrency(metrics.totalesGenerales.totalPrimaEmitida)}</span>
                                    <span className="text-slate-300">/</span>
                                    <span className="text-rose-500 font-bold">-{formatCurrency(metrics.totalesGenerales.totalPrimaAnulacion)}</span>
                                </div>
                            </div>

                            {/* RECUENTO PÓLIZAS EMITIDAS */}
                            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Pólizas Emitidas (Brutas)</span>
                                    <span className="p-2 bg-blue-50 rounded-xl"><IconDocumentCheck /></span>
                                </div>
                                <div>
                                    <h3 className="text-3xl font-black text-blue-600 font-mono">
                                        {metrics.totalesGenerales.totalEmitidasCount} <span className="text-xs text-slate-400 font-normal">pólizas</span>
                                    </h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">Altas en el Trimestre</p>
                                </div>
                            </div>

                            {/* RECUENTO PÓLIZAS ANULADAS */}
                            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Pólizas Anuladas</span>
                                    <span className="p-2 bg-rose-50 rounded-xl"><IconDocumentX /></span>
                                </div>
                                <div>
                                    <h3 className="text-3xl font-black text-rose-600 font-mono">
                                        {metrics.totalesGenerales.totalAnuladasCount} <span className="text-xs text-slate-400 font-normal">bajas</span>
                                    </h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">Caídas en el Trimestre</p>
                                </div>
                            </div>

                        </div>
                    )}

                    {/* TABLA: APORTE NETO POR COLABORADOR / VENDEDOR */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col w-full">
                        <div className="p-4 px-6 bg-slate-50 border-b flex items-center justify-between">
                            <div>
                                <h4 className="font-black text-slate-800 text-sm uppercase tracking-tight">
                                    Aporte Neto por Colaborador / Vendedor
                                </h4>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                                    Haz clic en cualquier fila para desplegar las pólizas emitidas y anuladas del colaborador
                                </p>
                            </div>
                            <button 
                                onClick={refetch}
                                className="px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition-all shadow-sm cursor-pointer"
                            >
                                🔄 Actualizar
                            </button>
                        </div>

                        <div className="overflow-x-auto w-full">
                            <table className="w-full text-xs border-collapse">
                                <thead className="bg-slate-50/50 text-slate-400 border-b uppercase text-[9px] font-black tracking-wider">
                                    <tr>
                                        <th className="p-4 pl-8 text-left">Colaborador / Vendedor</th>
                                        {metrics.isAgrupadoAutoMoto ? (
                                            <th className="p-3 text-center">Auto/Moto/Camión</th>
                                        ) : (
                                            <>
                                                <th className="p-3 text-center">Auto/Camión</th>
                                                <th className="p-3 text-center">Motos</th>
                                            </>
                                        )}
                                        <th className="p-3 text-center">Hogar</th>
                                        <th className="p-3 text-center">Comercio</th>
                                        <th className="p-3 text-center">AP</th>
                                        <th className="p-3 text-center">Salud</th>
                                        <th className="p-3 text-center">Caución</th>
                                        <th className="p-3 text-center">RC</th>
                                        <th className="p-4 text-right pr-8">Total Neto</th>
                                    </tr>
                                </thead>
                                <tbody className="font-semibold text-slate-700 divide-y divide-slate-100">
                                    {metrics.desgloseOperadores.length === 0 ? (
                                        <tr>
                                            <td colSpan="10" className="p-12 text-center text-slate-400 italic">
                                                No hay registros de producción de SANCOR para el filtro seleccionado.
                                            </td>
                                        </tr>
                                    ) : (
                                        metrics.desgloseOperadores.map(op => {
                                            const isExpanded = expandedColabName === op.nombre;

                                            return (
                                                <React.Fragment key={op.nombre}>
                                                    <tr 
                                                        onClick={() => toggleColabDetail(op.nombre)}
                                                        className={`transition-colors cursor-pointer select-none ${
                                                            isExpanded ? 'bg-blue-50/60 font-bold' : 'hover:bg-slate-50/70'
                                                        }`}
                                                    >
                                                        <td className="p-4 pl-8 text-slate-900 font-black uppercase text-sm flex items-center gap-3">
                                                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] transition-transform duration-200 ${
                                                                isExpanded ? 'bg-blue-600 text-white rotate-180' : 'bg-slate-100 text-slate-500'
                                                            }`}>
                                                                ▼
                                                            </span>
                                                            <span>{op.nombre}</span>
                                                        </td>

                                                        {metrics.isAgrupadoAutoMoto ? (
                                                            <td className="p-3 text-center font-mono font-bold text-indigo-600">{op.netoAutosMotos}</td>
                                                        ) : (
                                                            <>
                                                                <td className="p-3 text-center font-mono font-bold text-indigo-600">{op.netoAutosCamion}</td>
                                                                <td className="p-3 text-center font-mono font-bold text-sky-600">{op.netoMotos}</td>
                                                            </>
                                                        )}

                                                        <td className="p-3 text-center font-mono font-bold text-emerald-600">{op.netoHogar}</td>
                                                        <td className="p-3 text-center font-mono font-bold text-blue-600">{op.netoComercio}</td>
                                                        <td className="p-3 text-center font-mono font-bold text-amber-600">{op.netoAP}</td>
                                                        <td className="p-3 text-center font-mono font-bold text-rose-600">{op.netoSalud}</td>
                                                        <td className="p-3 text-center font-mono font-bold text-purple-600">{op.netoCaucion}</td>
                                                        <td className="p-3 text-center font-mono font-bold text-teal-600">{op.netoRC}</td>

                                                        <td className="p-4 text-right pr-8">
                                                            <div className="inline-flex flex-col items-end">
                                                                <span className="font-mono text-sm font-black text-slate-900 bg-slate-100 px-3 py-1 rounded-xl border border-slate-200/80">
                                                                    {op.totalNeto > 0 ? `+${op.totalNeto}` : op.totalNeto} netas
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>

                                                    {/* CAJÓN DESPLEGABLE INLINE */}
                                                    {isExpanded && (
                                                        <tr>
                                                            <td colSpan="10" className="p-0 bg-slate-50/80 border-y-2 border-blue-200 animate-fade-in">
                                                                <div className="p-6 space-y-4">
                                                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                                                        <div>
                                                                            <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest">
                                                                                Detalle de Auditoría Comercial
                                                                            </span>
                                                                            <h5 className="text-sm font-black text-slate-800 uppercase mt-0.5">
                                                                                Pólizas de {op.nombre}
                                                                            </h5>
                                                                        </div>

                                                                        <div className="flex items-center gap-2">
                                                                            <button
                                                                                onClick={() => setActiveDetailTab('ALTAS')}
                                                                                className={`px-3 py-1.5 rounded-lg text-xs font-black transition-all ${
                                                                                    activeDetailTab === 'ALTAS'
                                                                                        ? 'bg-emerald-500 text-white shadow-sm'
                                                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                                                }`}
                                                                            >
                                                                                🟢 Emitidas / Pases ({op.polizasAltas.length})
                                                                            </button>
                                                                            <button
                                                                                onClick={() => setActiveDetailTab('BAJAS')}
                                                                                className={`px-3 py-1.5 rounded-lg text-xs font-black transition-all ${
                                                                                    activeDetailTab === 'BAJAS'
                                                                                        ? 'bg-rose-500 text-white shadow-sm'
                                                                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                                                }`}
                                                                            >
                                                                                🔴 Anuladas ({op.polizasBajas.length})
                                                                            </button>
                                                                        </div>
                                                                    </div>

                                                                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                                                                        {activeDetailTab === 'ALTAS' ? (
                                                                            op.polizasAltas.length === 0 ? (
                                                                                <div className="p-8 text-center text-slate-400 italic text-xs">
                                                                                    No se registran emisiones cargadas en este período para este colaborador.
                                                                                </div>
                                                                            ) : (
                                                                                <table className="w-full text-xs text-left border-collapse">
                                                                                    <thead className="bg-slate-50 text-slate-400 uppercase text-[9px] font-black border-b">
                                                                                        <tr>
                                                                                            <th className="p-3 pl-6">N° Póliza</th>
                                                                                            <th className="p-3">Cliente / Asegurado</th>
                                                                                            <th className="p-3">Ramo</th>
                                                                                            <th className="p-3">Motivo / Tipo Operación</th>
                                                                                            <th className="p-3">Sub-Operación</th>
                                                                                            <th className="p-3">Fecha Carga</th>
                                                                                            <th className="p-3 text-right pr-6">Prima Emitida ($)</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                                                                                        {op.polizasAltas.map((p, idx) => (
                                                                                            <tr key={idx} className="hover:bg-slate-50/80">
                                                                                                <td className="p-3 pl-6 font-mono font-bold text-blue-600">{p.nPoliza || 'PENDIENTE'}</td>
                                                                                                <td className="p-3 font-bold uppercase text-slate-900">{p.clienteNombre}</td>
                                                                                                <td className="p-3 text-slate-500">{p.riesgo}</td>
                                                                                                <td className="p-3">
                                                                                                    <span className="px-2 py-0.5 rounded text-[10px] font-black bg-slate-100 text-slate-700">
                                                                                                        {p.motivo || 'POLIZA NUEVA'}
                                                                                                    </span>
                                                                                                </td>
                                                                                                <td className="p-3">
                                                                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-black ${p.subOperacion.includes('CAMBIO') ? 'bg-cyan-100 text-cyan-800' : 'bg-emerald-100 text-emerald-800'}`}>
                                                                                                        {p.subOperacion || 'EMISION NUEVA'}
                                                                                                    </span>
                                                                                                </td>
                                                                                                <td className="p-3 font-mono text-slate-400">{p.fechaCarga}</td>
                                                                                                <td className="p-3 text-right pr-6 font-mono font-bold text-emerald-600">
                                                                                                    {formatCurrency(p.primaEmitida)}
                                                                                                </td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            )
                                                                        ) : (
                                                                            op.polizasBajas.length === 0 ? (
                                                                                <div className="p-8 text-center text-slate-400 italic text-xs">
                                                                                    ¡Excelente! Sin pólizas anuladas para este colaborador.
                                                                                </div>
                                                                            ) : (
                                                                                <table className="w-full text-xs text-left border-collapse">
                                                                                    <thead className="bg-slate-50 text-slate-400 uppercase text-[9px] font-black border-b">
                                                                                        <tr>
                                                                                            <th className="p-3 pl-6">N° Póliza</th>
                                                                                            <th className="p-3">Cliente / Asegurado</th>
                                                                                            <th className="p-3">Ramo</th>
                                                                                            <th className="p-3">Motivo Operación</th>
                                                                                            <th className="p-3">Sub-Operación</th>
                                                                                            <th className="p-3">Fecha Anulación</th>
                                                                                            <th className="p-3 text-right pr-6">Prima Perdida ($)</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody className="divide-y divide-slate-100 font-semibold text-slate-700">
                                                                                        {op.polizasBajas.map((p, idx) => (
                                                                                            <tr key={idx} className="hover:bg-slate-50/80">
                                                                                                <td className="p-3 pl-6 font-mono font-bold text-rose-600">{p.nPoliza || 'S/N'}</td>
                                                                                                <td className="p-3 font-bold uppercase text-slate-900">{p.clienteNombre}</td>
                                                                                                <td className="p-3 text-slate-500">{p.riesgo}</td>
                                                                                                <td className="p-3">
                                                                                                    <span className="px-2 py-0.5 rounded text-[10px] font-black bg-rose-50 text-rose-700 border border-rose-100">
                                                                                                        {p.motivo || 'ANULACION'}
                                                                                                    </span>
                                                                                                </td>
                                                                                                <td className="p-3 text-slate-500 text-[11px]">
                                                                                                    {p.subOperacion || '-'}
                                                                                                </td>
                                                                                                <td className="p-3 font-mono text-rose-500 font-bold">{p.fechaAnulacion}</td>
                                                                                                <td className="p-3 text-right pr-6 font-mono font-bold text-rose-600">
                                                                                                    -{formatCurrency(p.primaAnulacion)}
                                                                                                </td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            <style>{`
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default KpisObjetivosSancor;